/**
 * Set Password — for admin-invited users
 * Route: /set-password?token=XXX
 */
import React, { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import client from "@/api/client";
import { toast } from "sonner";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_circularity-hub/artifacts/hwzjkab2_DecarbonIQ%20%2814%29.png";

export default function SetPassword() {
  const navigate      = useNavigate();
  const { login }     = useAuth();
  const [params]      = useSearchParams();
  const token         = params.get("token");
  const [password,    setPassword]    = useState("");
  const [confirmPwd,  setConfirmPwd]  = useState("");
  const [showPass,    setShowPass]    = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirmPwd) { setError("Passwords do not match."); return; }
    if (!token) { setError("Invalid invite link. Please contact your administrator."); return; }
    setLoading(true);
    setError("");
    try {
      const res = await client.post("/onboarding/set-password", { token, password });
      // Log in immediately — use window.location to force AuthContext re-init from localStorage
      localStorage.setItem("decarboniq_token", res.data.token);
      localStorage.setItem("decarboniq_user", JSON.stringify(res.data.user));
      toast.success("Password set. Welcome to DeCarbonIQ!");
      const role = res.data.user?.active_role || "dashboard";
      const ROLE_DEST = { sme: "/sme/journey", corporate: "/corporate/workspace", project: "/project/assessment", fi: "/fi/workspace" };
      window.location.href = ROLE_DEST[role] || "/dashboard";
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid or expired invite link. Please contact your administrator.");
    }
    setLoading(false);
  };

  if (!token) return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center text-center px-6">
      <div>
        <p className="text-white text-lg font-bold mb-2">Invalid invite link</p>
        <p className="text-slate-400 text-sm">This link is missing a security token. Please use the link from your invitation email.</p>
        <Link to="/login" className="mt-4 text-teal-400 text-sm hover:underline">Back to login</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center px-6"
      data-testid="set-password-page">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="bg-[#0B1220] rounded-xl px-4 py-2 inline-block mb-4">
            <img src={LOGO_URL} alt="DeCarbonIQ" className="h-7" style={{ filter: "brightness(0) invert(1)" }} />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Set Your Password</h1>
          <p className="text-slate-400 text-sm mt-1">Your account is ready. Set a password to access your workspace.</p>
        </div>

        <div className="bg-white rounded-xl p-8">
          {error && (
            <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">{error}</div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">New Password</label>
              <div className="relative">
                <input type={showPass ? "text" : "password"} value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="w-full h-11 px-4 pr-10 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  data-testid="set-password-input" />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Confirm Password</label>
              <input type="password" value={confirmPwd}
                onChange={e => setConfirmPwd(e.target.value)}
                placeholder="Repeat your password"
                className="w-full h-11 px-4 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                data-testid="confirm-password-input" />
            </div>
            <button type="submit" disabled={loading}
              className="w-full h-12 bg-teal-600 text-white text-sm font-bold rounded-xl hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2"
              data-testid="set-password-submit-btn">
              {loading ? "Setting up..." : <><CheckCircle2 size={15} /> Set Password & Enter Workspace</>}
            </button>
          </form>
        </div>

        <p className="mt-4 text-center text-xs text-slate-500">
          Problems with your invite? <Link to="/login" className="text-teal-400 hover:underline">Contact support</Link>
        </p>
      </div>
    </div>
  );
}
